'use client'

// The collection page's right sidebar: Sales Activity, plus a Social tab.
// One wrapper for every layout that shows the activity panel (NFT collections
// and fungible tokens, phone and desktop), so the tab exists once.

// Loaded for its capture of `?post=` before this page rewrites its query.
import '@/app/alkanes/lib/linked-post'
import React from 'react'
import dynamic from 'next/dynamic'
import { ChevronRight } from 'lucide-react'
import { Activity } from '@/app/collection/[id]/v2/activity'

// The social stack (composer, wallet signing, Twetch crypto) only loads when
// someone opens the tab — the trading page stays as light as it was.
const CollectionSocial = dynamic(() => import('./index'), {
	ssr: false,
	loading: () => <p className="px-4 py-8 text-center text-xs text-muted-foreground">Loading social…</p>
})

export interface SidebarTab {
	id: string
	label: string
	/// Rendered with the height left under the tab row.
	render: (height: string) => React.ReactNode
}

const TABS_HEIGHT_PX = 41
const TITLE_HEIGHT_PX = 45

export default function ActivitySocialPanel({
	slug,
	name,
	height,
	hideHeader,
	onCollapse,
	extraTabs = [],
	view: controlledView,
	onViewChange
}: {
	slug: string
	name?: string | null
	height: string
	hideHeader?: boolean
	/// Desktop drawer: shows the panel's "Activity" title bar, which collapses it.
	onCollapse?: () => void
	/// Page-specific views that sit between Sales and Social (NAT's holders).
	/// They join THIS tab row rather than bringing a second one.
	extraTabs?: SidebarTab[]
	/// Controlled selection, for pages that deep-link a tab (NAT's "view
	/// owners"). Uncontrolled when omitted.
	view?: string
	onViewChange?: (view: string) => void
}) {
	const [ownView, setOwnView] = React.useState<string>('activity')
	const view = controlledView ?? ownView
	const setView = (next: string) => {
		setOwnView(next)
		onViewChange?.(next)
	}
	const tabs: [string, string][] = [
		['activity', 'Sales'],
		...extraTabs.map((tab): [string, string] => [tab.id, tab.label]),
		['social', 'Social']
	]
	const extra = extraTabs.find((tab) => tab.id === view)
	const inner = `calc(${height} - ${TABS_HEIGHT_PX + (onCollapse ? TITLE_HEIGHT_PX : 0)}px)`
	return (
		<div data-testid="collection-activity-social">
			{onCollapse && (
				<button
					type="button"
					onClick={onCollapse}
					aria-label="Hide activity panel"
					className="flex h-11 w-full items-center justify-between border-b border-border px-4 text-left transition-colors hover:bg-muted/40"
				>
					<span className="text-sm font-bold">Activity</span>
					<ChevronRight className="size-4 text-muted-foreground" aria-hidden="true" />
				</button>
			)}
			<div className="flex h-10 items-stretch border-b border-border">
				<div
					role="tablist"
					aria-label="Sidebar view"
					className="grid min-w-0 flex-1"
					style={{ gridTemplateColumns: `repeat(${tabs.length}, minmax(0, 1fr))` }}
				>
					{tabs.map(([id, label]) => (
						<button
							key={id}
							type="button"
							role="tab"
							aria-selected={view === id}
							data-testid={`collection-sidebar-tab-${id}`}
							onClick={() => setView(id)}
							className={`-mb-px truncate border-b-2 px-2 text-xs font-bold outline-none transition-colors focus-visible:bg-muted/60 ${
								view === id
									? 'border-foreground text-foreground'
									: 'border-transparent text-muted-foreground hover:text-foreground'
							}`}
						>
							{label}
						</button>
					))}
				</div>
			</div>
			{extra ? (
				extra.render(inner)
			) : view === 'social' ? (
				<CollectionSocial slug={slug} name={name} height={inner} />
			) : (
				<Activity slug={slug} height={inner} hideHeader={hideHeader} />
			)}
		</div>
	)
}
